import { useCallback, useEffect, useMemo, useState } from 'react'
import { apiDocsApi, type ApiDocument, type ApiDocumentName } from '../api/apiDocs'
import { Page, PageHeader, FilterBar, Button, TextField, Tabs, Callout, LoadingState, Card, CardHeader, DataTable, StatusPill } from '../components/ui'

/**
 * F-112 (TASK-0366, REQ-045-001, REQ-291-001, REQ-289-001): API documentate (solo Admin). Le operazioni sono raggruppate
 * per modulo, con metodo, percorso e policy di autorizzazione; i due documenti OpenAPI (interne ed esterne) si possono
 * scaricare in JSON. Le API esterne (`/api/v1/*`) sono versionate: la regola di compatibilita' e' nella descrizione.
 */
const TABS: { key: ApiDocumentName; label: string }[] = [
  { key: 'interne', label: 'API interne' },
  { key: 'esterne', label: 'API esterne (/api/v1)' },
]

const METHOD_TONE: Record<string, 'success' | 'primary' | 'warning' | 'error' | 'neutral'> = {
  GET: 'success',
  POST: 'primary',
  PUT: 'warning',
  PATCH: 'warning',
  DELETE: 'error',
}

export function ApiDocsPage() {
  const [tab, setTab] = useState<ApiDocumentName>('interne')
  const [doc, setDoc] = useState<ApiDocument | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setDoc(await apiDocsApi.get(tab))
    } catch (e) {
      setDoc(null)
      setError(e instanceof Error ? e.message : 'Documento non disponibile')
    } finally {
      setLoading(false)
    }
  }, [tab])

  useEffect(() => { void load() }, [load])

  const groups = useMemo(() => {
    const needle = filter.trim().toLowerCase()
    const byTag = new Map<string, ApiDocument['operations']>()
    for (const op of doc?.operations ?? []) {
      if (needle && !`${op.method} ${op.path} ${op.tag} ${op.policies.join(' ')}`.toLowerCase().includes(needle)) continue
      const list = byTag.get(op.tag) ?? []
      list.push(op)
      byTag.set(op.tag, list)
    }
    return [...byTag.entries()]
  }, [doc, filter])

  const download = async () => {
    try {
      await apiDocsApi.download(tab)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Download non riuscito')
    }
  }

  return (
    <Page>
      <PageHeader
        title="API documentate"
        icon="api"
        actions={
          <Button variant="outlined" onClick={() => void download()} disabled={!doc}>
            Scarica JSON
          </Button>
        }
      />

      <FilterBar>
        <TextField
          type="search"
          label="Filtra per percorso, modulo o policy"
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
      </FilterBar>

      <div>
        <Tabs
          items={TABS.map(t => ({ id: t.key, label: t.label }))}
          value={tab as string}
          onChange={(newTab) => setTab(newTab as ApiDocumentName)}
          aria-label="API documenti"
        />
      </div>

      {error && <Callout tone="warning">{error}</Callout>}
      {loading && <LoadingState />}

      {!loading && doc && (
        <>
          <Callout tone="info">
            <div className="md-typescale-emphasized-body-medium">{doc.title} — versione {doc.version}</div>
            <div>{doc.description}</div>
            <div className="mt-1">{doc.operations.length.toLocaleString('it-IT')} operazioni.</div>
          </Callout>

          {groups.length === 0 && <p className="text-muted">Nessuna operazione corrisponde al filtro.</p>}
          {groups.map(([tag, ops]) => (
            <Card key={tag} variant="outlined" padding="none">
              <CardHeader title={tag} subtitle={`${ops.length}`} />
              <DataTable variant="plain">
                <thead>
                  <tr><th>Metodo</th><th>Percorso</th><th>Policy di autorizzazione</th></tr>
                </thead>
                <tbody>
                  {ops.map(op => (
                    <tr key={`${op.method} ${op.path}`}>
                      <td><StatusPill tone={METHOD_TONE[op.method] ?? 'neutral'}>{op.method}</StatusPill></td>
                      <td><code>{op.path}</code></td>
                      <td>
                        {op.policies.length === 0
                          ? <span className="text-muted">—</span>
                          : op.policies.map(p => <StatusPill key={p} tone="neutral" className="me-2">{p}</StatusPill>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </Card>
          ))}
        </>
      )}
    </Page>
  )
}
